import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { change, FieldArray, formValueSelector, reduxForm } from 'redux-form';
import { Button, Form } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import PassengerForm from './PassengerForm';
import { DEFAULT_FOOD_OPTION } from '../../../../config/defaults';
import { orderBy } from '../../../../utils/array';
import FormFooter from '../../../common/forms/FormFooter';
import PaymentForm from './PaymentForm';
import { isPinpadLocked } from '../../../common/forms/MultiPaymentMethodInputGroup';
import {
  changeStepBookingOnRoute,
  clearPassengerList,
  getRideBookingQuote,
  postPaymentOnRoute,
} from '../../../../actions';
import {
  CIVA_SALES_ON_ROUTE_BOOKING_SELECTION_SEAT_STEP_INDEX,
  CIVA_SALES_ON_ROUTE_BOOKING_STEPS,
  TRANSACTION_TYPE_CREDIT_NOTE,
} from '../../../../config/constants';
import {
  buildTransactionList,
  flagUsedPinpadTransactions,
  getRowTransactionCode,
  summarizeSplit,
} from '../../../../utils/units/paymentSplit';
import { tzNormalizeDate } from '../../../../utils/date';
import { TIMESTAMP_FORMAT } from '../../../../config/locale';
import { numberFormatter } from '../../../../utils/number';

const FORM_NAME = 'rideBookingPassengerForm';

const STALE_QUOTE = { stale: true };

const isUsableQuote = (quote) => Boolean(quote) && !quote.stale;

// what /ride-booking/quote reads: the seats, who travels in them and the code
const buildQuoteRequest = (seatReservationIds, passengers, discountCode) => ({
  reservationRequestList: seatReservationIds.map((seatReservationId) => {
    const row = (passengers || []).find(
      (item) =>
        item &&
        item.seatReservation &&
        item.seatReservation.value === seatReservationId,
    );
    const person = row ? row.passenger : null;

    return {
      seatReservationId,
      customer: person ? { id: person.id } : null,
      source: person ? person.source : null,
      discountCodeString: discountCode || null,
    };
  }),
});

const PINPAD_CHARGE_LEAVE_MESSAGE =
  'Hay un cobro aprobado en el pinpad que no se registró. Anúlelo con "Cancelar pago" antes de salir. ¿Desea salir de todas formas?';

const renderPassengers = ({ fields }) =>
  fields.map((passenger, index) => (
    <PassengerForm
      passenger={passenger}
      key={passenger}
      index={index}
      fields={fields}
      fieldsLength={fields.length}
    />
  ));

const RideBookingPassengerForm = ({
  handleSubmit,
  total,
  dispatchChange,
  dispatchPostPaymentOnRoute,
  processingPayment,
  dispatchChangeStepBookingOnRoute,
  allPassengersSelected,
  dispatchClearPassengerList,
  seatSelectionChangesInProgress,
  hasPinpadCharge,
  totalPosLoading,
  pinpadLocked,
  history,
  submitting,
  seatReservationIds,
  passengersValue,
  dispatchGetRideBookingQuote,
}) => {
  const [quoting, setQuoting] = useState(false);
  const [quoteFailed, setQuoteFailed] = useState(false);

  const mountedRef = useRef(true);
  const quoteSequenceRef = useRef(0);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // only the latest request may update the total, an older answer is stale
  const requestQuote = async (ids, passengers, discountCode) => {
    quoteSequenceRef.current += 1;
    const sequence = quoteSequenceRef.current;
    setQuoting(true);

    const quote = await dispatchGetRideBookingQuote(
      buildQuoteRequest(ids, passengers, discountCode),
    );

    if (!mountedRef.current || sequence !== quoteSequenceRef.current) {
      return STALE_QUOTE;
    }

    setQuoting(false);
    // any priced answer means the shown total is current again
    if (quote) setQuoteFailed(false);
    return quote;
  };

  // base total for the current seats, agency group discount included
  const refreshTotal = async () => {
    const quote = await requestQuote(seatReservationIds, passengersValue, null);
    if (quote && quote.stale) return;

    setQuoteFailed(!quote);
    if (quote) dispatchChange(FORM_NAME, 'total', quote.total);
  };

  const applyDiscountCode = async (discountCode) => {
    const quote = await requestQuote(
      seatReservationIds,
      passengersValue,
      discountCode,
    );
    // an invalid code keeps the current total, the backend error is shown
    if (!isUsableQuote(quote)) return;

    dispatchChange(FORM_NAME, 'total', quote.total);
    dispatchChange(FORM_NAME, 'appliedDiscountCode', discountCode);
    toastr.success('Éxito', 'Código de descuento aplicado correctamente.');
  };

  const clearDiscountCode = async () => {
    const quote = await requestQuote(seatReservationIds, passengersValue, null);
    if (!isUsableQuote(quote)) return;

    dispatchChange(FORM_NAME, 'discountCode', null);
    dispatchChange(FORM_NAME, 'appliedDiscountCode', null);
    dispatchChange(FORM_NAME, 'total', quote.total);
  };

  useEffect(() => {
    if (!hasPinpadCharge || processingPayment) return undefined;

    // the app history is v5, which react-router 4's <Prompt> can't block
    // (it passes a string where v5 expects a function), so block it directly
    const unblock = history.block((transition) => {
      // eslint-disable-next-line no-alert
      if (window.confirm(PINPAD_CHARGE_LEAVE_MESSAGE)) {
        unblock();
        transition.retry();
      }
    });

    return unblock;
  }, [hasPinpadCharge, processingPayment]);

  useLayoutEffect(
    () => () => {
      dispatchClearPassengerList();
    },
    [],
  );

  useEffect(() => {
    if (total === 0 || total === null) {
      dispatchChangeStepBookingOnRoute(
        CIVA_SALES_ON_ROUTE_BOOKING_STEPS[
          CIVA_SALES_ON_ROUTE_BOOKING_SELECTION_SEAT_STEP_INDEX
        ],
      );
    }
  }, [total]);

  const seatReservationIdsKey = seatReservationIds.join(',');

  useEffect(() => {
    if (!seatReservationIds.length) return;

    // a seat change invalidates the discounted total, the code must be applied again
    dispatchChange(FORM_NAME, 'discountCode', null);
    dispatchChange(FORM_NAME, 'appliedDiscountCode', null);
    refreshTotal();
  }, [seatReservationIdsKey]);

  const renderPassengerFields = (props) => renderPassengers(props);

  const validateString = (str) => str && String(str).trim().length > 0;

  const FIELD_LABEL = {
    firstName: 'nombre',
    lastName: 'apellido',
    identificationTypeId: 'tipo de documento',
    idDocumentNumber: 'número de documento',
    dob: 'fecha de nacimiento',
  };

  const validatePassenger = (passengers) => {
    const errors = [];

    passengers.forEach((item, index) => {
      if (!item) {
        errors.push(`Falta Completar el Pasajero ${index + 1}`);
        return;
      }

      const { passenger } = item;

      if (!passenger) {
        errors.push(`Falta información del Pasajero ${index + 1}`);
        return;
      }

      const missingFields = [];

      Object.keys(FIELD_LABEL).forEach((field) => {
        const value = passenger[field];

        if (!validateString(value)) {
          missingFields.push(FIELD_LABEL[field]);
        }
      });

      if (missingFields.length > 0) {
        errors.push(
          `Pasajero ${index + 1}: Falta completar ${missingFields.join(', ')}`,
        );
      }
    });

    return errors.reverse();
  };

  const onSubmit = async (formValues) => {
    // if (!formValues.phoneNumber || !/^\d{9}$/.test(formValues.phoneNumber)) {
    //   toastr.warning("El número de teléfono debe tener 9 dígitos y contener solo números.");
    //   return;
    // }

    const errors = validatePassenger(formValues.passengers);

    if (errors.length) {
      errors.forEach((error) => toastr.warning(error));
      return;
    }

    const payerIsPassenger =
      !!formValues.customer &&
      formValues.passengers.some(
        (item) =>
          item &&
          item.passenger &&
          item.passenger.value === formValues.customer.value,
      );

    if (!payerIsPassenger) {
      toastr.error(
        'Error',
        'El cliente que paga debe ser uno de los pasajeros',
      );
      return;
    }

    // only an applied code is sent, a typed but unapplied one is ignored
    // because the total doesn't include it
    const appliedDiscountCode = formValues.appliedDiscountCode || null;

    const totalAmount = parseFloat(formValues.total) || 0;
    const transactionRows = formValues.transactionList || [];

    // price again with the final passengers: a code can have customer rules
    const quote = await requestQuote(
      formValues.passengers.map((item) => item.seatReservation.value),
      formValues.passengers,
      appliedDiscountCode,
    );

    if (!isUsableQuote(quote)) return;

    if (Math.abs(quote.total - totalAmount) > 0.005) {
      dispatchChange(FORM_NAME, 'total', quote.total);
      const newTotal = numberFormatter({ style: 'currency', value: quote.total });
      toastr.warning(
        'Advertencia',
        hasPinpadCharge
          ? `El total cambió a ${newTotal}. Ajuste los montos de pago o anule el cobro del pinpad antes de confirmar.`
          : `El total cambió a ${newTotal}. Revise los montos de pago y vuelva a confirmar.`,
      );
      return;
    }

    const summary = summarizeSplit(transactionRows, totalAmount);

    if (summary.hasDuplicateMethods) {
      toastr.error(
        'Error',
        'No puede repetir el mismo método de pago en dos filas',
      );
      return;
    }

    if (!summary.isValid) {
      toastr.error(
        'Error',
        summary.isPending
          ? 'Los montos no cubren el total a pagar'
          : 'Los montos superan el total a pagar',
      );
      return;
    }

    const paysWithCreditNote = transactionRows.some(
      (row) => getRowTransactionCode(row) === TRANSACTION_TYPE_CREDIT_NOTE,
    );

    if (
      paysWithCreditNote &&
      (!formValues.customer ||
        formValues.customer.source !== 'CUSTOMER' ||
        formValues.passengers.length !== 1)
    ) {
      toastr.error(
        'Error',
        'La nota de crédito solo se puede usar con un pasajero y un cliente registrado',
      );
      return;
    }

    const { transactions } = buildTransactionList(transactionRows, {
      totalPrice: totalAmount,
      customerId: formValues.customer ? formValues.customer.id : null,
      voucherTypeId: formValues.voucherType
        ? formValues.voucherType.value
        : null,
      businessId: formValues.business ? formValues.business.value : null,
    });

    transactions.forEach((transaction) => {
      if (transaction.depositDate) {
        // eslint-disable-next-line no-param-reassign
        transaction.depositDate = tzNormalizeDate({
          date: transaction.depositDate,
          format: TIMESTAMP_FORMAT,
        });
      }
    });

    const reservationRequestList = formValues.passengers.map((passenger) => ({
      customer: {
        id: passenger.passenger.id,
      },
      source: passenger.passenger.source,
      foodId: passenger.food.value,
      seatReservationId: passenger.seatReservation.value,
      discountCodeString: appliedDiscountCode,
    }));

    const newFormValues = {
      reservationRequestList,
      customerId: formValues.customer ? formValues.customer.id : null,
      voucherTypeId: formValues.voucherType
        ? formValues.voucherType.value
        : null,
      // phoneNumber: formValues.phoneNumber,
      businessId: formValues.business ? formValues.business.value : null,
      passengerSource: formValues.customer.source,
      businessAddressSummary: formValues.addressSummary || null,
      businessTaxId: formValues.businessTaxId || null,
      businessName: formValues.businessName || null,
      transactionList: transactions,
      pinPadTransactions: flagUsedPinpadTransactions(
        formValues.pinPadTransactions || [],
        transactionRows,
      ),
    };

    dispatchPostPaymentOnRoute(newFormValues);
  };

  return (
    <div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="container">
          <FieldArray name="passengers" component={renderPassengerFields} />
        </div>
        <hr />
        <div className="container">
          <PaymentForm
            totalPrice={total}
            quoting={quoting}
            onApplyDiscountCode={applyDiscountCode}
            onClearDiscountCode={clearDiscountCode}
          />
        </div>
        {/* the total is shown by the payment split summary; the form value
            `total` stays as the quoted amount the payments must add up to */}
        {quoteFailed && (
          <div className="container text-danger mb-3">
            No se pudo calcular el total de la venta.{' '}
            <Button
              type="button"
              color="link"
              className="p-0 align-baseline"
              onClick={refreshTotal}
              disabled={quoting}
            >
              Reintentar
            </Button>
          </div>
        )}
        <div className="container">
          <FormFooter
            saveButtonDisabled={
              submitting ||
              quoting ||
              quoteFailed ||
              processingPayment ||
              totalPosLoading ||
              pinpadLocked ||
              !allPassengersSelected ||
              total === 0 ||
              seatSelectionChangesInProgress.size !== 0
            }
            saveButtonText={
              processingPayment ? 'Procesando pago...' : 'Confirmar Pago'
            }
          />
        </div>
      </Form>
    </div>
  );
};

RideBookingPassengerForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  processingPayment: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    passengers: PropTypes.arrayOf(
      PropTypes.shape({
        seatReservation: PropTypes.shape({
          value: PropTypes.number,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        idCountryOfOrigin: PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        }),
        identificationTypeId: PropTypes.shape({
          value: PropTypes.number,
          label: PropTypes.string,
        }),
        food: PropTypes.shape({
          value: PropTypes.number,
          label: PropTypes.string,
        }),
      }),
    ),
  }).isRequired,
  total: PropTypes.number.isRequired,
  dispatchPostPaymentOnRoute: PropTypes.func.isRequired,
  dispatchChangeStepBookingOnRoute: PropTypes.func.isRequired,
  allPassengersSelected: PropTypes.bool.isRequired,
  dispatchClearPassengerList: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  seatSelectionChangesInProgress: PropTypes.shape({
    size: PropTypes.number,
  }).isRequired,
  hasPinpadCharge: PropTypes.bool.isRequired,
  totalPosLoading: PropTypes.bool.isRequired,
  pinpadLocked: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    block: PropTypes.func.isRequired,
  }).isRequired,
  submitting: PropTypes.bool.isRequired,
  seatReservationIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  passengersValue: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dispatchGetRideBookingQuote: PropTypes.func.isRequired,
};

const selector = formValueSelector('rideBookingPassengerForm');

const mapStateToProps = (state) => {
  const { BookingUnit, TotalPosUnit } = state;

  const seatReservations = BookingUnit.Booking.getIn([
    'seats',
    'seatReservations',
  ]).toJS();

  const seatReservationsOrdered = orderBy(
    seatReservations,
    ['itinerary.departureTime', 'itineraryId', 'seat.seatNumber'],
    ['asc', 'asc'],
  );

  const passengers = seatReservationsOrdered.map((seatReservation) => ({
    seatReservation: {
      value: seatReservation.id,
      label: seatReservation.seat.seatNumber,
      itinerary: seatReservation.itinerary || null,
      itineraryId: seatReservation.itineraryId || null,
      seatNumber: seatReservation.seat.seatNumber,
      floorNumber: seatReservation.seat.floorNumber,
    },
    food: DEFAULT_FOOD_OPTION,
  }));

  const total = BookingUnit.Booking.getIn(['pricing', 'total']);

  const processingPayment = BookingUnit.Booking.getIn([
    'payment',
    'postingPayment',
  ]);

  const passengersValue = selector(state, 'passengers') || [];

  const seatSelectionChangesInProgress = BookingUnit.Booking.getIn([
    'seats',
    'seatSelectionChangesInProgress',
  ]);

  return {
    initialValues: {
      passengers,
    },
    total: total || 0,
    processingPayment,
    seatReservationIds: seatReservationsOrdered.map(
      (seatReservation) => seatReservation.id,
    ),
    passengersValue,
    allPassengersSelected:
      passengersValue.length > 0 &&
      passengersValue.every((item) => item && item.passenger),
    seatSelectionChangesInProgress,
    hasPinpadCharge: (selector(state, 'transactionList') || []).some(
      (row) => row && row.pinpadPaid,
    ),
    totalPosLoading: !TotalPosUnit.TotalPos.getIn([
      'current',
      'activity',
    ]).isEmpty(),
    pinpadLocked: isPinpadLocked('rideBookingPassengerForm')(state),
  };
};

const mapDispatchToProps = {
  dispatchPostPaymentOnRoute: postPaymentOnRoute,
  dispatchChangeStepBookingOnRoute: changeStepBookingOnRoute,
  dispatchClearPassengerList: clearPassengerList,
  dispatchChange: change,
  dispatchGetRideBookingQuote: getRideBookingQuote,
};

const form = reduxForm({
  form: 'rideBookingPassengerForm',
})(RideBookingPassengerForm);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(form),
);
